import React, { useState, useEffect } from 'react';
import api from '../http';
import LicenseScanner from './LicenseScanner';

/**
 * Rapid-fire registration form.
 * Company names are auto-numbered; program name carries over between companies.
 * Flow: Add Company N → confirm program → scan candidates → Next Candidate or Change Company.
 */
export default function CandidateRegistrationForm({ event, onCandidateAdded }) {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [lastProgram, setLastProgram] = useState('');
  const [newProgram, setNewProgram] = useState('');

  const [view, setView] = useState('company'); // company | scan | scanner | form | success
  const [scannedData, setScannedData] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [successName, setSuccessName] = useState('');

  const [recentCandidates, setRecentCandidates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  const loadCompanies = async () => {
    try {
      const res = await api.get(`/api/registration/events/${event.id}/companies`);
      const list = res.data;
      setCompanies(list);
      // Seed lastProgram from the most recently added company that has one
      const recent = [...list].reverse().find((c) => c.program_name);
      if (recent?.program_name) setLastProgram(recent.program_name);
    } catch (e) {
      console.error('Failed to load companies', e);
    }
  };

  const loadRecentCandidates = async (companyId) => {
    try {
      const res = await api.get(
        `/api/registration/events/${event.id}/candidates?company_id=${companyId}`
      );
      setRecentCandidates(res.data.slice(0, 10));
    } catch (e) {
      console.error('Failed to load candidates', e);
    }
  };

  const nextCompanyNumber = companies.length + 1;

  const handleOpenAddForm = () => {
    setNewProgram(lastProgram); // pre-fill from last used
    setShowAddForm(true);
    setError('');
  };

  const handleCreateCompany = async () => {
    setError('');
    try {
      const res = await api.post(`/api/registration/events/${event.id}/companies`, {
        name: `Company ${nextCompanyNumber}`,
        program_name: newProgram.trim() || undefined
      });
      const company = res.data;
      setCompanies((prev) => [...prev, company]);
      if (newProgram.trim()) setLastProgram(newProgram.trim());
      setShowAddForm(false);
      handleSelectCompany(company);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create company');
    }
  };

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setShowAddForm(false);
    loadRecentCandidates(company.id);
    setView('scan');
  };

  const handleScanResult = (parsed) => {
    setScannedData(parsed);
    setFirstName(parsed.firstName || '');
    setLastName(parsed.lastName || '');
    setView('form');
  };

  const handleManualEntry = () => {
    setScannedData(null);
    setFirstName('');
    setLastName('');
    setView('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        scan_data: scannedData
          ? {
              firstName: scannedData.firstName,
              lastName: scannedData.lastName,
              middleName: scannedData.middleName,
              dateOfBirth: scannedData.dateOfBirth,
              licenseNumber: scannedData.licenseNumber,
              issuingState: scannedData.issuingState,
              scannedAt: new Date().toISOString()
            }
          : undefined
      };

      const res = await api.post(
        `/api/registration/events/${event.id}/companies/${selectedCompany.id}/candidates`,
        payload
      );

      const candidate = res.data;
      setSuccessName(`${firstName.trim()} ${lastName.trim()}`);
      setRecentCandidates((prev) => [
        { ...candidate, company_name: selectedCompany.name },
        ...prev.slice(0, 9)
      ]);
      if (onCandidateAdded) onCandidateAdded(candidate);
      setView('success');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to register candidate');
    } finally {
      setSaving(false);
    }
  };

  const handleNextCandidate = () => {
    setSuccessName('');
    setScannedData(null);
    setFirstName('');
    setLastName('');
    setView('scan');
    loadRecentCandidates(selectedCompany.id);
  };

  const handleChangeCompany = () => {
    setSelectedCompany(null);
    setScannedData(null);
    setView('company');
    setRecentCandidates([]);
  };

  // ── Company selection view ──────────────────────────────────────────────
  if (view === 'company') {
    return (
      <div className="registration-section">
        <h2 className="section-title">
          {companies.length === 0 ? 'Start Registration' : 'Select or Add Company'}
        </h2>

        {companies.length > 0 && (
          <div className="company-list">
            {companies.map((c) => (
              <button
                key={c.id}
                className="company-card"
                onClick={() => handleSelectCompany(c)}
              >
                <span className="company-card-name">{c.name}</span>
                {c.program_name && (
                  <span className="company-card-dept">{c.program_name}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {!showAddForm ? (
          <button
            className="btn btn-primary btn-block add-company-btn"
            onClick={handleOpenAddForm}
          >
            + Add Company {nextCompanyNumber}
          </button>
        ) : (
          <div className="new-company-form">
            <div className="new-company-label">Company {nextCompanyNumber}</div>
            <div className="form-group">
              <label className="form-label">Program Name</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Fire Academy 2024"
                value={newProgram}
                onChange={(e) => setNewProgram(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCompany()}
                autoFocus
              />
            </div>
            {error && <p className="form-error">{error}</p>}
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleCreateCompany}>
                Start Scanning →
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowAddForm(false); setError(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Company header used in all subsequent views ─────────────────────────
  const CompanyHeader = ({ showChange = true }) => (
    <div className="company-header">
      <span className="company-badge">{selectedCompany.name}</span>
      {selectedCompany.program_name && (
        <span className="company-program">{selectedCompany.program_name}</span>
      )}
      {showChange && (
        <button className="btn-link" onClick={handleChangeCompany}>Change</button>
      )}
    </div>
  );

  // ── Scan / Manual choice ────────────────────────────────────────────────
  if (view === 'scan') {
    return (
      <div className="registration-section">
        <CompanyHeader />
        <h2 className="section-title">Add Candidate</h2>

        <div className="scan-options">
          <button className="btn btn-primary btn-scan" onClick={() => setView('scanner')}>
            <span className="scan-icon">▣</span> Scan Driver's License
          </button>
          <button className="btn btn-outline" onClick={handleManualEntry}>
            Enter Name Manually
          </button>
        </div>

        {recentCandidates.length > 0 && (
          <div className="recent-candidates">
            <h4>Registered so far</h4>
            <ul className="candidate-list">
              {recentCandidates.map((c) => (
                <li
                  key={c.id}
                  className={`candidate-item ${c.documents_signed_at ? 'signed' : 'pending'}`}
                >
                  <span className="candidate-name">{c.last_name}, {c.first_name}</span>
                  <span className="candidate-status">
                    {c.documents_signed_at ? '✓ Signed' : '⏳ Awaiting signature'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // ── Camera scanner ──────────────────────────────────────────────────────
  if (view === 'scanner') {
    return (
      <div className="registration-section">
        <CompanyHeader showChange={false} />
        <LicenseScanner
          onScan={handleScanResult}
          onCancel={() => setView('scan')}
        />
      </div>
    );
  }

  // ── Confirm / manual entry form ─────────────────────────────────────────
  if (view === 'form') {
    return (
      <div className="registration-section">
        <CompanyHeader />
        <h2 className="section-title">
          {scannedData ? 'Confirm Scanned Info' : 'Candidate Details'}
        </h2>
        {scannedData && (
          <p className="scan-source-note">
            Name auto-populated from license scan — correct if needed.
          </p>
        )}

        <form onSubmit={handleSubmit} className="candidate-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                className="form-input"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus={!scannedData}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                className="form-input"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Register Candidate'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setView('scan')}
              disabled={saving}
            >
              Back
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Success ─────────────────────────────────────────────────────────────
  if (view === 'success') {
    return (
      <div className="registration-section">
        <CompanyHeader showChange={false} />
        <div className="signing-confirmed">
          <div className="signing-checkmark">✓</div>
          <h3>{successName}</h3>
          <p className="signing-status-text">
            Registered — direct them to scan the event QR code to sign documents.
          </p>
          <button className="btn btn-primary" onClick={handleNextCandidate}>
            Next Candidate
          </button>
        </div>
      </div>
    );
  }

  return null;
}
